import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon } from 'lucide-react';
export function Menu() {
  const {
    t
  } = useLanguage();
  const categories = Object.keys(t.menu.categories) as Array<keyof typeof t.menu.categories>;
  const [activeCategory, setActiveCategory] = useState<keyof typeof t.menu.categories>(categories[0]);
  return <section id="menu" className="py-32 lg:py-44 relative overflow-hidden" data-id="element-162">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full z-0" data-id="element-163">
        <video autoPlay loop muted playsInline className="object-cover w-full h-full opacity-40 scale-105" data-id="element-164">
          <source src="https://videos.pexels.com/video-files/3209211/3209211-uhd_2560_1440_25fps.mp4" type="video/mp4" data-id="element-165" />
        </video>
        <div className="absolute inset-0 bg-cream/90 backdrop-blur-[3px]" data-id="element-166" />
        <div className="absolute inset-0 bg-sand-texture opacity-50" data-id="element-167" />
      </div>

      <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-10 relative z-10" data-id="element-168">
        {/* Header */}
        <div className="text-center mb-20" data-id="element-169">
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="flex items-center justify-center gap-5 mb-6" data-id="element-170">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-sand/50" data-id="element-171"></div>
            <span className="text-sand text-[11px] font-medium tracking-[0.35em] uppercase" data-id="element-172">
              {t.menu.subtitle}
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-sand/50" data-id="element-173"></div>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1,
          duration: 0.8
        }} className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-navy" data-id="element-174">
            {t.menu.title}
          </motion.h2>
        </div>

        <div className="glass-card p-8 md:p-12 lg:p-16 rounded-xs" data-id="element-175">
          {/* Category Tabs */}
          <div className="flex overflow-x-auto hide-scrollbar justify-start md:justify-center gap-3 md:gap-4 mb-12 pb-2" data-id="element-176">
            {categories.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`whitespace-nowrap px-5 py-2.5 text-[11px] font-medium tracking-[0.2em] uppercase transition-all duration-500 rounded-full border ${activeCategory === category ? 'bg-navy text-white border-navy shadow-md' : 'bg-transparent text-navy/50 border-sand/20 hover:border-sand/50 hover:text-navy'}`} data-id="element-177">
                {t.menu.categories[category]}
              </button>)}
          </div>

          {/* Decorative Divider */}
          <div className="flex items-center justify-center gap-4 mb-12" data-id="element-178">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-sand/30" data-id="element-179"></div>
            <StarIcon className="w-3 h-3 text-sand/40" data-id="element-180" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-sand/30" data-id="element-181"></div>
          </div>

          {/* Menu Items */}
          <div className="min-h-[400px] relative" data-id="element-182">
            {/* Desktop Column Divider */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-sand/15 to-transparent -translate-x-1/2" data-id="element-183"></div>

            <AnimatePresence mode="wait" data-id="element-184">
              <motion.div key={activeCategory} initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -15
            }} transition={{
              duration: 0.5,
              ease: [0.4, 0, 0.2, 1]
            }} className="grid grid-cols-1 md:grid-cols-2 gap-x-24 gap-y-12" data-id="element-185">
                {t.menu.items[activeCategory].map((item, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.08,
                duration: 0.8,
                ease: [0.4, 0, 0.2, 1]
              }} className={`flex flex-col group p-4 -m-4 rounded-sm transition-all duration-500 hover:bg-sand/[0.03] hover:scale-[1.01] ${item.recommended ? 'relative' : ''}`} data-id="element-186">
                    {item.recommended && <div className="absolute -left-2 top-4 bottom-4 w-0.5 bg-sand/30 rounded-full" data-id="element-187"></div>}
                    <div className="flex justify-between items-baseline mb-2 gap-4" data-id="element-188">
                      <h3 className="font-serif text-xl text-navy flex items-center flex-wrap gap-x-2 gap-y-1 group-hover:text-navy-light transition-colors duration-500" data-id="element-189">
                        {item.num && <span className="editorial-number" data-id="element-190">{item.num}</span>}
                        {item.name}
                        {item.recommended && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sand/10 text-sand text-[9px] uppercase tracking-wider font-sans ml-1" data-id="element-191">
                            <StarIcon className="w-2.5 h-2.5 fill-sand" data-id="element-192" />
                            Recomendado
                          </span>}
                      </h3>
                      <div className="flex-grow border-b border-solid border-sand/10 mx-3 min-w-[20px] transition-colors duration-500 group-hover:border-sand/30" data-id="element-193"></div>
                      <div className="flex items-center flex-shrink-0" data-id="element-194">
                        {item.pricePerUnit && <span className="text-[9px] uppercase tracking-[0.15em] text-dark/30 mr-3 mt-0.5" data-id="element-195">
                            {t.menu.pricePerUnit}
                          </span>}
                        <span className="font-sans font-medium text-sand/90 text-[14px] tabular-nums whitespace-nowrap tracking-wide" data-id="element-196">
                          {item.price}
                        </span>
                      </div>
                    </div>
                    {item.desc && <p className="text-dark/50 font-light text-[15px] leading-[1.85] pr-12" data-id="element-197">
                        {item.desc}
                      </p>}
                  </motion.div>)}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 section-divider z-10" data-id="element-198" />
    </section>;
}